/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import { GameLaunchStatus, type StreamStatus } from "..";
import { EON_VIDEO_ID } from "../constants/webrtc";

/**
 * Extended MouseEvent with synthetic movement values for gamepad simulation.
 */
export interface SyntheticMouseEvent extends MouseEvent {
  isSyntheticMouseEvent: true;
  syntheticMovementX: number;
  syntheticMovementY: number;
}

export enum MouseSimulationMode {
  /** Joystick position maps directly to screen position (original behavior) */
  ABSOLUTE = "absolute",
  /** Drag joystick and release to move mouse by fixed pixels (gesture-based) */
  GESTURE = "gesture",
}

/**
 * MouseSimulator - Handles mouse simulation for gamepad inputs
 * Used when the game is loading to provide mouse-like interaction via gamepad
 */
export class MouseSimulator {
  private mousePosition: { x: number; y: number } = { x: 0, y: 0 };
  private targetPosition: { x: number; y: number } = { x: 0, y: 0 };
  private previousMousePosition: { x: number; y: number } = { x: 0, y: 0 }; //  for movement delta
  private animationFrameId: number | null = null;
  private videoElement: HTMLElement | null = null;
  private lastVideoDimensions: { width: number; height: number } = {
    width: 0,
    height: 0,
  };
  private streamStatus: StreamStatus | undefined;
  private _shouldSimulate = false; // Cached result, updated in setStreamStatus

  // Mode settings
  private mode: MouseSimulationMode = MouseSimulationMode.ABSOLUTE;

  // Gesture mode settings
  private lastJoystickState: { x: number; y: number } = { x: 0, y: 0 };
  private wasJoystickActive = false;
  private gestureMovementAnimationId: number | null = null;
  private currentDirection: { x: number; y: number } = { x: 0, y: 0 };
  private joystickMagnitude = 0; // Current joystick tilt magnitude
  private baseSpeed = 0; // Base pixels per frame at the reference rate (30fps)
  private lastFrameTime = 0; // Timestamp of previous rAF tick (for delta-time)
  private cachedVideoRect: DOMRect | null = null; // Cached rect, updated via updateVideoRect()
  private cachedVideoElement: HTMLElement | null = null;

  // flag to bypass the this._shouldSimulate on setStreamStatus
  private forceSimulate = false;
  /**
   * Direct callback set by InputHandler to receive synthetic mouse moves
   * without going through the video element's DOM event dispatch (which
   * triggers browser media side-effects like timeupdate).
   */
  public onSyntheticMouseMove:
    | ((
        movementX: number,
        movementY: number,
        clientX: number,
        clientY: number,
      ) => void)
    | null = null;

  public onGestureStart: (() => void) | null = null;

  // Debug
  public debug = false;
  private _dbgGestureStartTime = 0; // when startGestureContinuousMovement was called
  private _dbgLastAxisTime = 0; // when simulateMouseMovementGesture was last called
  private _dbgLastDispatchTime = 0; // when dispatchGestureMouseEvent last fired
  private _dbgDispatchCount = 0; // dispatches since last gesture start

  constructor(streamStatus?: StreamStatus, private readonly isTV = false) {
    this.streamStatus = streamStatus;
    this._shouldSimulate = this.computeShouldSimulate();
  }

  private getVideoElement(): HTMLElement | null {
    if (this.videoElement && this.videoElement.isConnected) {
      return this.videoElement;
    }
    this.videoElement = document.getElementById(EON_VIDEO_ID);
    return this.videoElement;
  }

  /**
   * Refresh the cached video element bounding rect.
   * Called by InputHandler.updateMapping() on resize/orientation change.
   */
  public updateVideoRect(): void {
    const videoElement = this.getVideoElement();
    if (!videoElement) return;

    this.cachedVideoRect = videoElement.getBoundingClientRect();
    this.cachedVideoElement = videoElement;
  }

  /**
   * Set the mouse simulation mode
   */
  setMode(mode: MouseSimulationMode): void {
    this.mode = mode;
  }

  /**
   * Update stream status (needed to determine if mouse simulation should be active)
   */
  setStreamStatus(streamStatus: StreamStatus | undefined): void {
    this.streamStatus = streamStatus;
    this._shouldSimulate = this.forceSimulate
      ? true
      : this.computeShouldSimulate();

    // Stop animations if mouse simulation is no longer needed
    if (!this._shouldSimulate) {
      this.stop();
    }
  }

  /**
   * Toggle mouse simulation mode
   */
  toggleMouseSimulation(): void {
    this.forceSimulate = !this._shouldSimulate ? true : false;
    this._shouldSimulate = !this._shouldSimulate;

    if (!this._shouldSimulate) {
      this.stop();
    }
  }

  /**
   * Check if mouse events should be simulated based on stream status
   */
  shouldSimulateMouseEvents(): boolean {
    return this._shouldSimulate;
  }

  shouldShowRemotePointer(): boolean {
    return this.computeShouldSimulate() || this._shouldSimulate;
  }

  private computeShouldSimulate(): boolean {
    if (!this.streamStatus?.status) return false;
    const statusValues = Object.values(GameLaunchStatus);
    const currentIndex = statusValues.indexOf(this.streamStatus.status);
    const launchingIndex = statusValues.indexOf(
      GameLaunchStatus.STATUS_GAMERUNNING,
    );
    return currentIndex !== -1 && currentIndex < launchingIndex;
  }

  /**
   * Smooth animation loop using linear interpolation
   */
  private animateMousePosition = (): void => {
    const rect = this.cachedVideoRect;
    if (!rect) return;

    // Lerp factor: higher = faster response, lower = smoother
    const lerpFactor = 0.5;

    // Calculate distance to target
    const deltaX = this.targetPosition.x - this.mousePosition.x;
    const deltaY = this.targetPosition.y - this.mousePosition.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Stop animation if we've reached the target (within 0.5px threshold)
    if (distance < 0.5) {
      this.mousePosition.x = this.targetPosition.x;
      this.mousePosition.y = this.targetPosition.y;
      this.animationFrameId = null;
      return;
    }

    // Movement delta
    const movementX = deltaX * lerpFactor;
    const movementY = deltaY * lerpFactor;

    // Interpolate current position towards target position
    this.mousePosition.x += movementX;
    this.mousePosition.y += movementY;

    // Dispatch mouse event
    const dotX = rect.left + this.mousePosition.x;
    const dotY = rect.top + this.mousePosition.y;

    if (this.onSyntheticMouseMove) {
      this.onSyntheticMouseMove(movementX, movementY, dotX, dotY);
    } else {
      const videoElement = this.getVideoElement();
      if (videoElement) {
        const mouseEvent = new MouseEvent("mousemove", {
          clientX: dotX,
          clientY: dotY,
          bubbles: false,
          cancelable: true,
          screenX: rect.width,
          screenY: rect.height,
          buttons: 0,
          view: window,
        }) as SyntheticMouseEvent;
        mouseEvent.isSyntheticMouseEvent = true;
        mouseEvent.syntheticMovementX = movementX;
        mouseEvent.syntheticMovementY = movementY;
        videoElement.dispatchEvent(mouseEvent);
      }
    }

    // Continue animation
    this.animationFrameId = requestAnimationFrame(this.animateMousePosition);
  };

  /**
   * Simulate mouse movement based on joystick input
   */
  simulateMouseMovement(joystickX: number, joystickY: number): void {
    if (this.mode === MouseSimulationMode.GESTURE) {
      this.simulateMouseMovementGesture(joystickX, joystickY);
    } else {
      this.simulateMouseMovementAbsolute(joystickX, joystickY);
    }
  }

  /**
   * ABSOLUTE MODE: Joystick position maps directly to screen position (original)
   */
  private simulateMouseMovementAbsolute(
    joystickX: number,
    joystickY: number,
  ): void {
    const videoElement = this.getVideoElement();
    if (!videoElement) return;

    const rect = videoElement.getBoundingClientRect();

    // Check if video dimensions changed (rotation/resize)
    if (
      this.lastVideoDimensions.width !== rect.width ||
      this.lastVideoDimensions.height !== rect.height
    ) {
      const oldWidth = this.lastVideoDimensions.width;
      const oldHeight = this.lastVideoDimensions.height;

      // Scale existing positions proportionally to new dimensions
      if (oldWidth > 0 && oldHeight > 0) {
        const scaleX = rect.width / oldWidth;
        const scaleY = rect.height / oldHeight;
        this.mousePosition.x *= scaleX;
        this.mousePosition.y *= scaleY;
        this.targetPosition.x *= scaleX;
        this.targetPosition.y *= scaleY;
      }

      // Update stored dimensions
      this.lastVideoDimensions = {
        width: rect.width,
        height: rect.height,
      };
    }

    // Since joystick is circular, we need to scale
    // based on the direction to ensure edge of circle reaches edge of rectangle
    const angle = Math.atan2(joystickY, joystickX);

    // Calculate how far we can go in this direction on the rectangle
    // The limiting factor is either width or height depending on the angle
    const aspectRatio = rect.width / rect.height;

    // Scale factor to make circular joystick reach rectangular bounds
    const scaleFactor = Math.min(
      Math.abs(Math.cos(angle)) > 0.001 ? 1 / Math.abs(Math.cos(angle)) : 1,
      Math.abs(Math.sin(angle)) > 0.001
        ? aspectRatio / Math.abs(Math.sin(angle))
        : 1,
    );

    // Apply scaled mapping
    const scaledX = joystickX * Math.min(scaleFactor, 2); // Cap scaling to prevent over-extension
    const scaledY = joystickY * Math.min(scaleFactor, 2);

    // Calculate position and clamp to video bounds
    const calculatedX = rect.width / 2 + (scaledX * rect.width) / 2;
    const calculatedY = rect.height / 2 + (scaledY * rect.height) / 2;

    // Set target position (animation loop will smoothly interpolate to it)
    this.targetPosition.x = Math.max(0, Math.min(rect.width, calculatedX));
    this.targetPosition.y = Math.max(0, Math.min(rect.height, calculatedY));

    // Start animation loop if not already running
    if (this.animationFrameId === null) {
      // Initialize current position to target on first call
      if (this.mousePosition.x === 0 && this.mousePosition.y === 0) {
        this.mousePosition.x = this.targetPosition.x;
        this.mousePosition.y = this.targetPosition.y;
      }
      this.animationFrameId = requestAnimationFrame(this.animateMousePosition);
    }
  }

  /**
   * GESTURE MODE: Hold joystick to continuously move in that direction
   * Supports continuous 360° angles based on joystick position
   */
  private simulateMouseMovementGesture(
    joystickX: number,
    joystickY: number,
  ): void {
    if (this.debug) {
      this._dbgLastAxisTime = performance.now();
    }

    if (!this.cachedVideoRect) {
      this.updateVideoRect();
    }
    const rect = this.cachedVideoRect;
    if (!rect) return;

    // Calculate base speed as pixels per frame (assuming 30fps)
    // 1.0% of smaller dimension per frame = ~30% per second at 30fps
    // TV gets a 1.5× boost — viewed from further away, needs faster traversal
    const baseMultiplier = this.isTV ? 0.015 : 0.01;
    this.baseSpeed = Math.min(rect.width, rect.height) * baseMultiplier;

    // Initialize mouse position to center on first call
    if (this.mousePosition.x === 0 && this.mousePosition.y === 0) {
      this.mousePosition.x = rect.width / 2;
      this.mousePosition.y = rect.height / 2;

      this.targetPosition.x = this.mousePosition.x;
      this.targetPosition.y = this.mousePosition.y;

      this.previousMousePosition.x = this.mousePosition.x;
      this.previousMousePosition.y = this.mousePosition.y;
    }

    // Detect if joystick is active
    const activationThreshold = 0.3;
    const isJoystickActive =
      Math.abs(joystickX) > activationThreshold ||
      Math.abs(joystickY) > activationThreshold;

    // Calculate joystick magnitude (0.0 to ~1.4 for diagonals)
    const rawMagnitude = Math.sqrt(
      joystickX * joystickX + joystickY * joystickY,
    );
    // Normalize to 0.0 - 1.0 range (considering diagonal max is ~1.4)
    this.joystickMagnitude = Math.min(rawMagnitude / 1.4, 1.0);

    // Determine direction from current joystick state
    // Use actual joystick values for continuous angle support
    const directionThreshold = 0.15;
    let directionX = 0;
    let directionY = 0;

    if (Math.abs(joystickX) > directionThreshold) {
      directionX = joystickX;
    }
    if (Math.abs(joystickY) > directionThreshold) {
      directionY = joystickY;
    }

    // Normalize direction vector to ensure consistent speed across all angles
    const directionMagnitude = Math.sqrt(
      directionX * directionX + directionY * directionY,
    );
    if (directionMagnitude > 0) {
      directionX = directionX / directionMagnitude;
      directionY = directionY / directionMagnitude;
    }

    // Update current direction
    this.currentDirection.x = directionX;
    this.currentDirection.y = directionY;

    // Start continuous movement when joystick becomes active
    if (!this.wasJoystickActive && isJoystickActive) {
      if (this.debug) {
        console.log(
          `[mouse-sim] gesture ACTIVATED | joystick:(${joystickX.toFixed(
            3,
          )}, ${joystickY.toFixed(3)})`,
        );
      }
      this.onGestureStart?.();
      this.startGestureContinuousMovement();
    }

    // Stop movement when joystick is released
    if (this.wasJoystickActive && !isJoystickActive) {
      if (this.debug) {
        console.log(
          `[mouse-sim] gesture STOPPED | dispatches:${
            this._dbgDispatchCount
          } | avg-interval:${
            this._dbgDispatchCount > 1
              ? (
                  (performance.now() - this._dbgGestureStartTime) /
                  this._dbgDispatchCount
                ).toFixed(1)
              : "n/a"
          }ms`,
        );
      }
      this.stopGestureContinuousMovement();
    }

    // Store current joystick state for next iteration
    this.lastJoystickState.x = joystickX;
    this.lastJoystickState.y = joystickY;
    this.wasJoystickActive = isJoystickActive;
  }

  /**
   * Start continuous movement in gesture mode using requestAnimationFrame
   */
  private startGestureContinuousMovement(): void {
    // Clear existing animation if any
    this.stopGestureContinuousMovement();

    if (this.debug) {
      this._dbgGestureStartTime = performance.now();
      this._dbgLastDispatchTime = 0;
      this._dbgDispatchCount = 0;
    }

    // Ensure rect cache is warm before the first tick
    if (!this.cachedVideoRect) {
      this.updateVideoRect();
    }

    // Initialise lastFrameTime so the immediate first tick gets dt=1ms
    // (lower clamp) rather than a huge delta from lastFrameTime=0.
    const now = performance.now();
    this.lastFrameTime = now;

    // Run the first tick immediately so the cursor starts moving in the same
    // frame the joystick became active, eliminating the 1-frame (~16ms) startup dead time.
    this.updateGestureMovement(now);
  }

  /**
   * Stop continuous movement in gesture mode
   */
  private stopGestureContinuousMovement(): void {
    if (this.gestureMovementAnimationId !== null) {
      cancelAnimationFrame(this.gestureMovementAnimationId);
      this.gestureMovementAnimationId = null;
    }
  }

  /**
   * Update gesture movement using delta-time scaling so cursor speed is
   * consistent regardless of device frame rate (30fps, 60fps, 120fps, etc.).
   */
  private updateGestureMovement = (timestamp: number): void => {
    const rect = this.cachedVideoRect;
    if (!rect) return;

    const targetInterval = this.isTV ? 1000 / 30 : 1000 / 60;
    const elapsed = timestamp - this.lastFrameTime;

    if (elapsed < targetInterval) {
      this.gestureMovementAnimationId = requestAnimationFrame(
        this.updateGestureMovement,
      );
      return;
    }

    // Delta time since last frame, clamped to [1, 100]ms.
    // The lower bound avoids division-by-zero on the first immediate call.
    // The upper bound prevents a single huge jump after a tab becomes visible
    // again (rAF was suspended while backgrounded).
    const dt = Math.min(Math.max(timestamp - this.lastFrameTime, 1), 33);
    this.lastFrameTime = timestamp;

    this.updateGesturePosition(dt);
    this.dispatchGestureMouseEvent(rect);

    // Continue
    this.gestureMovementAnimationId = requestAnimationFrame(
      this.updateGestureMovement,
    );
  };

  /**
   * Update gesture position scaled by actual elapsed time (dt) so cursor
   * speed is frame-rate independent.
   */
  private updateGesturePosition(dt: number): void {
    const directionX = this.currentDirection.x;
    const directionY = this.currentDirection.y;

    // baseSpeed is defined as pixels-per-frame at 30fps reference.
    // Normalise to pixels-per-ms then scale by actual dt.
    const referenceFrameMs = 1000 / 30;
    const magnitudeSquared = this.joystickMagnitude * this.joystickMagnitude;
    const speedMultiplier = 0.4 + magnitudeSquared * 3.1; // Range: 0.4 to 3.5
    const speed = (this.baseSpeed / referenceFrameMs) * dt * speedMultiplier;

    // TV: Apply boundary clamping to prevent edge accumulation
    // PC: Use unclamped positioning for pointer lock accuracy
    if (this.isTV) {
      const rect = this.cachedVideoRect;
      if (!rect) return;

      this.targetPosition.x = Math.max(
        0,
        Math.min(rect.width, this.targetPosition.x + directionX * speed),
      );
      this.targetPosition.y = Math.max(
        0,
        Math.min(rect.height, this.targetPosition.y + directionY * speed),
      );
    } else {
      this.targetPosition.x += directionX * speed;
      this.targetPosition.y += directionY * speed;
    }

    // Update mouse position directly (unclamped)
    this.mousePosition.x = this.targetPosition.x;
    this.mousePosition.y = this.targetPosition.y;
  }

  /**q
   * Dispatch mouse event for gesture mode (called at 60fps)
   */
  private dispatchGestureMouseEvent(rect: DOMRect): void {
    const videoElement = this.cachedVideoElement;
    if (!videoElement) return;

    // Movement delta (from unclamped positions for correct pointer-lock deltas)
    const movementX = this.mousePosition.x - this.previousMousePosition.x;
    const movementY = this.mousePosition.y - this.previousMousePosition.y;

    if (this.debug) {
      const now = performance.now();
      this._dbgDispatchCount++;
      if (this._dbgDispatchCount === 1) {
        console.log(
          `[mouse-sim] dispatch #1 | Δx:${movementX.toFixed(
            2,
          )} Δy:${movementY.toFixed(2)} | lag-from-start:${(
            now - this._dbgGestureStartTime
          ).toFixed(1)}ms | lag-from-axis:${(
            now - this._dbgLastAxisTime
          ).toFixed(1)}ms`,
        );
      } else {
        const interval = this._dbgLastDispatchTime
          ? now - this._dbgLastDispatchTime
          : 0;
        console.log(
          `[mouse-sim] dispatch #${
            this._dbgDispatchCount
          } | Δx:${movementX.toFixed(2)} Δy:${movementY.toFixed(
            2,
          )} | interval:${interval.toFixed(1)}ms | lag-from-axis:${(
            now - this._dbgLastAxisTime
          ).toFixed(1)}ms`,
        );
      }
      this._dbgLastDispatchTime = now;
    }

    this.previousMousePosition.x = this.mousePosition.x;
    this.previousMousePosition.y = this.mousePosition.y;

    // Clamp to video bounds only for the event's clientX/clientY
    const clampedX = Math.max(0, Math.min(rect.width, this.mousePosition.x));
    const clampedY = Math.max(0, Math.min(rect.height, this.mousePosition.y));

    const dotX = rect.left + clampedX;
    const dotY = rect.top + clampedY;

    // Use direct callback when available — avoids dispatching on the <video>
    // element which causes the browser to flush queued media events (timeupdate)
    // synchronously, adding ~30ms/sec of overhead at 60fps.
    if (this.onSyntheticMouseMove) {
      this.onSyntheticMouseMove(movementX, movementY, dotX, dotY);
      return;
    }

    const mouseEvent = new MouseEvent("mousemove", {
      clientX: dotX,
      clientY: dotY,
      bubbles: false,
      cancelable: true,
      screenX: window.screenX + dotX,
      screenY: window.screenY + dotY,
      buttons: 0,
      view: window,
    }) as SyntheticMouseEvent;
    // Mark as synthetic event and add movement values for pointer lock mode
    mouseEvent.isSyntheticMouseEvent = true;
    mouseEvent.syntheticMovementX = movementX;
    mouseEvent.syntheticMovementY = movementY;
    videoElement.dispatchEvent(mouseEvent);
  }

  /**
   * Simulate mouse click events (mousedown/mouseup)
   */
  simulateMouseEvent(
    type: "mousedown" | "mouseup",
    button: "left" | "right",
  ): void {
    const videoElement = this.getVideoElement();
    if (!videoElement) return;

    const movementX = this.mousePosition.x - this.previousMousePosition.x;
    const movementY = this.mousePosition.y - this.previousMousePosition.y;

    this.previousMousePosition.x = this.mousePosition.x;
    this.previousMousePosition.y = this.mousePosition.y;

    const rect = videoElement.getBoundingClientRect();

    const dotX = rect.left + this.mousePosition.x;
    const dotY = rect.top + this.mousePosition.y;

    const mouseEvent = new MouseEvent(type, {
      clientX: dotX,
      clientY: dotY,
      bubbles: false,
      cancelable: false,
      screenX: rect.width,
      screenY: rect.height,
      button: button === "left" ? 0 : 2,
      view: window,
    }) as SyntheticMouseEvent;

    mouseEvent.isSyntheticMouseEvent = true;
    mouseEvent.syntheticMovementX = movementX;
    mouseEvent.syntheticMovementY = movementY;

    videoElement.dispatchEvent(mouseEvent);
  }

  /**
   * Sync internal position from a real (non-synthetic) mouse event.
   * This keeps the simulator in sync with the actual cursor position,
   * so that future gamepad deltas are calculated from the correct starting point.
   */
  syncPositionFromRealMouse(localX: number, localY: number): void {
    const videoElement = this.getVideoElement();
    if (!videoElement) return;

    const rect = videoElement.getBoundingClientRect();

    // Always set the simulator position to the real mouse's local
    // coordinates. In pointer lock mode, localX/localY is the frozen
    // lock-point position, which is the correct anchor for the gamepad
    // to resume from. Movement deltas for the remote cursor are already
    // handled separately in InputHandler.updateMousePosition.
    const clampedX = Math.max(0, Math.min(rect.width, localX));
    const clampedY = Math.max(0, Math.min(rect.height, localY));

    this.mousePosition.x = clampedX;
    this.mousePosition.y = clampedY;
    this.targetPosition.x = clampedX;
    this.targetPosition.y = clampedY;
    this.previousMousePosition.x = clampedX;
    this.previousMousePosition.y = clampedY;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.stopGestureContinuousMovement();
    this.cachedVideoElement = null;
    this.videoElement = null;
    this.forceSimulate = false;
  }

  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.stopGestureContinuousMovement();
    this.forceSimulate = false;
  }
}
